import { PlexUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import { CircularProgress, createStyles, makeStyles, Theme, Tooltip } from '@material-ui/core';
import MuiArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import MuiArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import MuiDragHandleIcon from '@material-ui/icons/DragHandle';
import React from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';

import { ReactComponent as EditSvg } from '../../../assets/edit.svg';
import { BoardOverviewFragment, FeedQuestionDetailsFragment, PlexOverviewFragment } from '../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../theme';
import { ButtonColor, PlexButton } from '../../shared/primitives/PlexButton';
import { IconButtonSize, PlexIconButton } from '../../shared/primitives/PlexIconButton';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { PlexName } from '../common/PlexName';
import { useSelectedPlexQuestion } from '../details/question/useSelectedQuestion';
import { canChangeBoardOrder, canEditBoard } from '../permissions';
import { NewQuestionButton } from '../post_form/NewQuestionButton';
import { QuestionView } from './QuestionView';

type Props = {
  board: BoardOverviewFragment;
  plexData: PlexOverviewFragment;
  plexRelation: PlexUserRelationState;
  totalFaqCount: number;
  loadingMore: boolean;
  onFetchMore: (nextCursor: number) => void;
  nextCursor?: number | null;
  collapsed: boolean;
  onCollapseClicked: (event: React.MouseEvent<HTMLElement, MouseEvent>, boardId: string, collapsed: boolean) => void;
  questionsArr: FeedQuestionDetailsFragment[];
  order: number[];
  onEditBoard: () => void;
  onSelectPlexQuestion: (permalink: string) => void;
  onMoveCard: (dragIndex: number, hoverIndex: number) => void;
  onDrop: (question: FeedQuestionDetailsFragment, atIndex: number) => void;
  onDragEnd: () => void;
  draggableProvided?: DraggableProvided | null;
};

export function BoardView(props: Props): React.ReactElement | null {
  const {
    board,
    order,
    draggableProvided,
    onSelectPlexQuestion,
    plexRelation,
    plexData,
    totalFaqCount,
    nextCursor,
    collapsed,
    onCollapseClicked,
    onEditBoard,
    onMoveCard,
    onDrop,
    onDragEnd,
    questionsArr,
    loadingMore,
    onFetchMore,
  } = props;
  const classes = useStyles();
  const [selectedPlexQuestion] = useSelectedPlexQuestion();

  return (
    <div {...draggableProvided?.draggableProps} ref={draggableProvided?.innerRef}>
      <div className={classes.root} style={{ borderColor: plexData.color || '#dcdcdc' }}>
        <div className={classes.boardTitleContainer}>
          <div className={classes.boardTitleLeft}>
            {canChangeBoardOrder(plexRelation) && draggableProvided != null && (
              <span {...draggableProvided.dragHandleProps}>
                <MuiDragHandleIcon />
              </span>
            )}
            <PlexIconButton
              MuiIcon={collapsed ? MuiArrowDropUpIcon : MuiArrowDropDownIcon}
              muiButtonClasses={{ root: classes.collapseButton }}
              onClick={(event): void => onCollapseClicked(event, board.id, !collapsed)}
              color={ButtonColor.TransparentBlack}
              tooltip={
                collapsed
                  ? 'Show questions'
                  : 'Hide questions (Tip: hold the ALT key while clicking to hide all of them)'
              }
              size={IconButtonSize.Normal}
            />

            <PlexName name={plexData.name} color={plexData.color} />

            <Tooltip title={board.description}>
              <PlexTypography className={classes.boardName} size={TextSizesEnum.Large}>
                {`${board.name} (${questionsArr.length} / ${totalFaqCount})`}
              </PlexTypography>
            </Tooltip>
            {canEditBoard(plexRelation) && (
              <PlexIconButton
                SvgIcon={EditSvg}
                className={classes.editButton}
                onClick={onEditBoard}
                color={ButtonColor.TransparentBlack}
                tooltip={'Edit'}
              />
            )}
          </div>
          <div>
            <NewQuestionButton board={board} />
          </div>
        </div>

        {!collapsed && order.length > 0 && (
          <div className={classes.questionsContainer}>
            {order.map((ind, i) => {
              const question = questionsArr[ind];
              return (
                <QuestionView
                  id={`plexQuestion_${question.id}`}
                  key={question.id}
                  question={question}
                  onSelect={onSelectPlexQuestion}
                  plexData={plexData}
                  selected={selectedPlexQuestion?.permalink === question.permalink}
                  visible={true}
                  index={i}
                  moveCard={onMoveCard}
                  onDrop={onDrop}
                  onDragEnd={onDragEnd}
                  boardId={board.id}
                />
              );
            })}
          </div>
        )}
        {!collapsed &&
          (loadingMore ? (
            <CircularProgress className={classes.loader} size={48} color={'inherit'} />
          ) : (
            !!nextCursor && (
              <div>
                <PlexButton
                  className={classes.loadMoreButton}
                  label={'Load More'}
                  color={ButtonColor.White}
                  onClick={(): void => {
                    onFetchMore(nextCursor);
                  }}
                />
              </div>
            )
          ))}
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexShrink: 0,
      flexDirection: 'column',

      backgroundColor: '#f3f3f3',
      borderWidth: 2,
      borderStyle: 'solid',
      borderRadius: 6,
      boxShadow: 'none',
      marginBottom: spacings.normal,
      paddingBottom: spacings.normal,
      marginLeft: spacings.normal,
      marginRight: spacings.normal,
    },
    boardTitleContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginLeft: spacings.normal,
      marginRight: spacings.xlarge,
      marginTop: spacings.normal,
    },
    boardTitleLeft: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    boardName: {
      fontWeight: 'bold',
      color: colorPalette.dark._800,
      alignItems: 'center',
      marginLeft: spacings.normal,
    },
    questionsContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexFlow: 'wrap',
      marginLeft: spacings.small,
      marginRight: spacings.small,
      marginTop: spacings.small,
      marginBottom: -spacings.small,
    },
    editButton: {
      display: 'inline', // needed for the edit button to be displayed at the end of the text
      marginLeft: spacings.small,
    },
    collapseButton: {
      marginRight: spacings.small,
      padding: 4,
    },
    loader: {
      margin: spacings.normal,
      color: '#fff',
    },
    loadMoreButton: {
      margin: spacings.normal,
    },
  })
);
